import type { LegacyDescription } from "encamp-shared/src/toscanini/constants";

import { areDependenciesMet } from "encamp-shared/src/toscanini";
import { ActivityStatus, ActivityType } from "generated-graphql/graphql";
import { useMemo } from "react";

type WorkflowActivity = {
  id: string;
  type: ActivityType;
  description?: string | null;
  status: ActivityStatus;
};

type DependencyResult = {
  canComplete: boolean;
  pendingDependencies: string[];
  tooltipMessage: string | null;
};

/**
 * Hook to check if a staff activity can be completed based on its dependencies.
 *
 * @param activityDescription - The description of the staff activity
 * @param workflowActivities - All activities in the workflow
 * @returns Object with canComplete boolean, pending dependencies, and tooltip message
 */
export function useActivityDependencies(
  activity: WorkflowActivity | undefined | null,
  workflowActivities: WorkflowActivity[]
): DependencyResult {
  return useMemo(() => {
    if (
      !activity?.description ||
      activity.type !== ActivityType.Staff ||
      activity.status === ActivityStatus.InProgress
    ) {
      return {
        canComplete: activity?.status === ActivityStatus.InProgress,
        pendingDependencies: [],
        tooltipMessage: null,
      };
    }

    // Convert workflow activities to the format expected by areDependenciesMet
    const activitiesForCheck = workflowActivities.map((a) => ({
      name: a.type,
      description: a.description ?? "",
      status: a.status,
    }));

    // The areDependenciesMet function handles unknown activity descriptions
    // gracefully by returning no dependencies (allowing completion).
    // We cast to LegacyDescription since the function internally handles
    // the case where the description doesn't match a known activity.
    const result = areDependenciesMet(
      activity?.description as LegacyDescription,
      activitiesForCheck
    );

    if (result.met) {
      return {
        canComplete: true,
        pendingDependencies: [],
        tooltipMessage: null,
      };
    }

    const pendingNames = result.pending.map((dep) => String(dep));

    return {
      canComplete: false,
      pendingDependencies: pendingNames,
      tooltipMessage: `Waiting for: ${pendingNames.join(", ") || "Prior Activity"}`,
    };
  }, [activity, workflowActivities]);
}
